import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, Input, Button, message, Modal, Tag, Tabs, Progress } from 'antd';
import { UploadOutlined, SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import mammoth from 'mammoth';
import { api, shotApi } from '../services/api';

const { TextArea } = Input;
const { TabPane } = Tabs;

// 解析分集
const parseEpisodes = (text) => {
  const episodes = {};
  const episodeStartPattern = /^\s*第([一二三四五六七八九十\d]+)集[:：]?\s*$/i;
  
  const lines = text.split('\n');
  let currentEpisodeTitle = null;
  let currentEpisodeContent = [];
  
  for (const line of lines) {
    const startMatch = line.match(episodeStartPattern);
    if (startMatch) {
      const episodeNum = startMatch[1];
      const episodeTitle = `第${episodeNum}集`;
      
      if (currentEpisodeTitle && currentEpisodeContent.length > 0) {
        episodes[currentEpisodeTitle] = currentEpisodeContent.join('\n').trim();
      }
      
      currentEpisodeTitle = episodeTitle;
      currentEpisodeContent = [];
      continue;
    }
    
    if (currentEpisodeTitle) {
      currentEpisodeContent.push(line);
    }
  }
  
  if (currentEpisodeTitle && currentEpisodeContent.length > 0) {
    episodes[currentEpisodeTitle] = currentEpisodeContent.join('\n').trim();
  }
  
  return episodes;
};

const ScriptPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // State
  const [script, setScript] = useState('');
  const [episodes, setEpisodes] = useState({});
  const [episodeList, setEpisodeList] = useState([]);
  const [activeEpisode, setActiveEpisode] = useState('all');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isProgressModalVisible, setIsProgressModalVisible] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState({ current: 0, total: 0, episode_title: '' });
  const [lastSavedContent, setLastSavedContent] = useState('');
  const [isGeneratingStoryboard, setIsGeneratingStoryboard] = useState(false);
  const [isStoryboardModalVisible, setIsStoryboardModalVisible] = useState(false);
  const [storyboardProgress, setStoryboardProgress] = useState({ current: 0, total: 0, episode_title: '' });
  
  // 分镜组规划状态
  const [isGeneratingShotGroups, setIsGeneratingShotGroups] = useState(false);
  const [isShotGroupsModalVisible, setIsShotGroupsModalVisible] = useState(false);
  const [shotGroupsProgress, setShotGroupsProgress] = useState({ current: 0, total: 0, episode_title: '' });

  // Refs
  const fileInputRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  const storyboardIntervalRef = useRef(null);
  const shotGroupsIntervalRef = useRef(null);

  // 加载剧本
  useEffect(() => {
    const loadScript = async () => {
      try {
        const response = await api.get(`/projects/${id}/script`);
        const content = response.data.content || '';
        setScript(content);
        
        const parsedEpisodes = parseEpisodes(content);
        setEpisodes(parsedEpisodes);
        setEpisodeList(Object.keys(parsedEpisodes));
      } catch (error) {
        message.error('加载剧本失败');
      }
    };
    
    if (id) {
      loadScript();
    }
  }, [id]);

  // 自动保存
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  // 清理进度轮询
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  // 清理分镜轮询
  useEffect(() => {
    return () => {
      if (storyboardIntervalRef.current) {
        clearInterval(storyboardIntervalRef.current);
      }
    };
  }, []);

  // 文件上传
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // 添加剧本
  const handleAddScriptClick = () => {
    if (window.addFileInput) {
      window.addFileInput.click();
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      let content = '';
      
      if (file.name.endsWith('.docx')) {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        content = result.value;
      } else {
        content = await file.text();
      }
      
      setScript(content);
      
      const parsedEpisodes = parseEpisodes(content);
      setEpisodes(parsedEpisodes);
      setEpisodeList(Object.keys(parsedEpisodes));
      setActiveEpisode('all');
      
      message.success(`已读取文件: ${file.name}，共 ${Object.keys(parsedEpisodes).length} 集`);
    } catch (error) {
      message.error('读取文件失败: ' + error.message);
    }
    
    event.target.value = '';
  };

  // 处理添加剧本文件
  const handleAddFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      let content = '';
      
      if (file.name.endsWith('.docx')) {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        content = result.value;
      } else {
        content = await file.text();
      }
      
      // 解析新剧本的集数
      const newEpisodes = parseEpisodes(content);
      const newEpisodeList = Object.keys(newEpisodes);
      
      // 解析现有剧本的集数
      const existingEpisodes = parseEpisodes(script);
      
      // 合并剧本，处理集数冲突
      let mergedContent = script;
      const mergedEpisodes = { ...existingEpisodes };
      
      // 检查是否有集数冲突
      const conflictingEpisodes = newEpisodeList.filter(episode => existingEpisodes.hasOwnProperty(episode));
      
      if (conflictingEpisodes.length > 0) {
        // 有冲突，覆盖现有集数
        message.warning(`发现 ${conflictingEpisodes.length} 个集数冲突，将覆盖现有内容: ${conflictingEpisodes.join('、')}`);
        
        // 构建新的合并内容
        let newContent = '';
        const allEpisodes = [...new Set([...Object.keys(existingEpisodes), ...newEpisodeList])];
        
        // 按集数顺序排序
        allEpisodes.sort((a, b) => {
          const numA = a.match(/第(\d+)集/)?.[1] || 0;
          const numB = b.match(/第(\d+)集/)?.[1] || 0;
          return parseInt(numA) - parseInt(numB);
        });
        
        // 构建新内容
        allEpisodes.forEach(episode => {
          if (newEpisodes.hasOwnProperty(episode)) {
            // 使用新剧本的内容
            newContent += `第${episode.replace('第', '').replace('集', '')}集\n${newEpisodes[episode]}\n\n`;
            mergedEpisodes[episode] = newEpisodes[episode];
          } else {
            // 保留现有内容
            newContent += `第${episode.replace('第', '').replace('集', '')}集\n${existingEpisodes[episode]}\n\n`;
          }
        });
        
        mergedContent = newContent.trim();
      } else {
        // 无冲突，在现有剧本下方追加
        mergedContent += '\n\n' + content;
        
        // 合并集数
        Object.keys(newEpisodes).forEach(episode => {
          mergedEpisodes[episode] = newEpisodes[episode];
        });
      }
      
      // 更新状态
      setScript(mergedContent);
      setEpisodes(mergedEpisodes);
      setEpisodeList(Object.keys(mergedEpisodes));
      setActiveEpisode('all');
      
      message.success(`已添加文件: ${file.name}，共 ${newEpisodeList.length} 集`);
    } catch (error) {
      message.error('读取文件失败: ' + error.message);
    }
    
    event.target.value = '';
  };

  // 剧本编辑
  const handleScriptChange = (e) => {
    const newContent = e.target.value;
    setScript(newContent);
    
    const parsedEpisodes = parseEpisodes(newContent);
    setEpisodes(parsedEpisodes);
    setEpisodeList(Object.keys(parsedEpisodes));
    
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // 使用 ref 保存最新内容，避免闭包问题
    autoSaveTimerRef.current = setTimeout(() => {
      // 获取最新的 script 值
      setScript(currentScript => {
        if (currentScript !== lastSavedContent && id) {
          autoSaveScript(currentScript);
        }
        return currentScript;
      });
    }, 3000);
  };
  
  // 手动保存剧本
  const handleSaveScript = async () => {
    if (!id) {
      message.error('项目ID为空，无法保存');
      return;
    }
    if (!script.trim()) {
      message.warning('剧本内容为空');
      return;
    }
    try {
      await autoSaveScript(script);
      message.success('剧本保存成功');
    } catch (error) {
      message.error('保存失败: ' + (error.response?.data?.error || error.message));
    }
  };

  const autoSaveScript = async (content) => {
    console.log('[autoSaveScript] 尝试保存剧本, 项目ID:', id);
    if (!id) {
      console.error('[autoSaveScript] 项目ID为空，无法保存');
      return;
    }
    try {
      const parsedEpisodes = parseEpisodes(content);
      const episodeList = Object.keys(parsedEpisodes);
      
      console.log('[autoSaveScript] 发送保存请求...');
      await api.post(`/projects/${id}/script`, { 
        project_id: id, 
        content: content, 
        episodes: episodeList 
      });
      
      setLastSavedContent(content);
      console.log('[autoSaveScript] 保存成功');
    } catch (error) {
      console.error('[autoSaveScript] 自动保存失败:', error);
      message.error('保存失败: ' + (error.response?.data?.error || error.message));
    }
  };

  // 剧本分析
  const pollAnalysisTaskStatus = useCallback(async (taskId) => {
    try {
      console.log('Polling task status for:', taskId);
      const response = await api.get(`/ai/analyze-script/status/${taskId}`);
      console.log('API response:', response.data);
      const { status, progress, result, error } = response.data;
      
      console.log('Task status:', status);
      console.log('Task progress:', progress);
      console.log('Task result:', result);
      console.log('Task error:', error);
      
      // 确保进度信息被正确更新
      if (progress) {
        console.log('Updating analysis progress:', progress);
        setAnalysisProgress(progress);
        // 手动更新UI显示的进度百分比
        if (progress.current !== undefined && progress.total !== undefined && progress.total > 0) {
          const percentage = Math.round((progress.current / progress.total) * 100);
          console.log('Calculated progress percentage:', percentage);
        }
      }
      
      if (status === 'completed') {
        console.log('Task completed, clearing interval...');
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        console.log('Setting modal visibility...');
        setIsProgressModalVisible(false);
        setIsAnalyzing(false);
        setAnalysisResult(result);
        setIsModalVisible(true);
        
        const charCount = result?.characters?.length || 0;
        const propCount = result?.props?.length || 0;
        const sceneCount = result?.scenes?.length || 0;
        
        message.success(`分析完成！识别到 ${charCount} 个角色、${propCount} 个道具、${sceneCount} 个场景`);
        
        window.dispatchEvent(new CustomEvent('assets-updated', { 
          detail: { 
            projectId: id, 
            characters: result?.characters || [],
            props: result?.props || [],
            scenes: result?.scenes || []
          } 
        }));
      } else if (status === 'failed') {
        console.log('Task failed, clearing interval...');
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        setIsProgressModalVisible(false);
        setIsAnalyzing(false);
        
        // 检查是否是AI未配置错误或额度用完错误
        if (error && (error.includes('AI未配置') || error.includes('API密钥') || error.includes('FreeTierOnly') || error.includes('AllocationQuota') || error.includes('分析结果为空'))) {
          Modal.error({
            title: 'AI配置或额度问题',
            content: (
              <div>
                <p>{error}</p>
                <p>请前往「项目设置」页面配置大模型API密钥或检查额度后再试。</p>
              </div>
            ),
            okText: '去配置',
            onOk: () => {
              // 跳转到项目设置页面
              navigate(`/project/${id}/settings`);
            }
          });
        } else if (error && error.includes('保存任务结果失败')) {
          // 任务结果太长导致的错误，但是资产已经生成成功
          console.log('任务结果太长，但是资产已经生成成功');
          message.success('资产分析完成！请前往资产管理页面查看生成的资产');
          
          // 发送资产更新事件
          window.dispatchEvent(new CustomEvent('assets-updated', { 
            detail: { 
              projectId: id,
              characters: [],
              props: [],
              scenes: []
            } 
          }));
        } else {
          message.error('剧本分析失败: ' + (error || '未知错误'));
        }
      }
    } catch (error) {
      console.error('获取任务状态失败:', error);
      // 处理API调用失败的情况
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      setIsProgressModalVisible(false);
      setIsAnalyzing(false);
      message.error('获取任务状态失败，请稍后重试');
    }
  }, [id]);

  const handleAnalyzeScript = async () => {
    if (!script.trim()) {
      message.warning('请先输入或上传剧本内容');
      return;
    }
    
    console.log('Starting analyze script...');
    
    // 首先清除之前可能存在的轮询
    if (progressIntervalRef.current) {
      console.log('Clearing existing interval...');
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    
    // 检查是否已存在资产
    const hasAssets = await checkExistingAssets();
    
    if (hasAssets) {
      Modal.confirm({
        title: '确认操作',
        content: '数据库里已经存在资产，是否重新执行识别资产命令？您的这个命令将会覆盖之前的数据。',
        okText: '确认执行',
        cancelText: '取消',
        onOk: async () => {
          console.log('User confirmed to analyze script');
          setIsAnalyzing(true);
          setIsProgressModalVisible(true);
          
          const parsedEpisodes = parseEpisodes(script);
          const episodeList = Object.keys(parsedEpisodes);
          const totalEpisodes = episodeList.length;
          
          console.log(`Found ${totalEpisodes} episodes`);
          setAnalysisProgress({ current: 0, total: totalEpisodes, episode_title: `正在分析剧本（共${totalEpisodes}集）...` });
          
          try {
            console.log('Sending POST request to /ai/analyze-script');
            const startResponse = await api.post('/ai/analyze-script', {
              script,
              project_id: id
            });
            
            console.log('Response from server:', startResponse.data);
            const { task_id } = startResponse.data;
            console.log('Received task_id:', task_id);
            
            // 确保清除之前的轮询
            if (progressIntervalRef.current) {
              console.log('Clearing interval before setting new one...');
              clearInterval(progressIntervalRef.current);
              progressIntervalRef.current = null;
            }
            
            console.log('Setting up polling for task:', task_id);
            progressIntervalRef.current = setInterval(() => {
              pollAnalysisTaskStatus(task_id);
            }, 1000);
            
          } catch (error) {
            console.error('Error starting analysis:', error);
            if (progressIntervalRef.current) {
              clearInterval(progressIntervalRef.current);
              progressIntervalRef.current = null;
            }
            setIsProgressModalVisible(false);
            setIsAnalyzing(false);
            const errorMsg = error.response?.data?.error || error.message;
            message.error('启动分析失败: ' + errorMsg);
          }
        }
      });
    } else {
      console.log('No existing assets found, starting analysis directly');
      setIsAnalyzing(true);
      setIsProgressModalVisible(true);
      
      const parsedEpisodes = parseEpisodes(script);
      const episodeList = Object.keys(parsedEpisodes);
      const totalEpisodes = episodeList.length;
      
      console.log(`Found ${totalEpisodes} episodes`);
      setAnalysisProgress({ current: 0, total: totalEpisodes, episode_title: `正在分析剧本（共${totalEpisodes}集）...` });
      
      try {
        console.log('Sending POST request to /ai/analyze-script');
        const startResponse = await api.post('/ai/analyze-script', {
          script,
          project_id: id
        });
        
        console.log('Response from server:', startResponse.data);
        const { task_id } = startResponse.data;
        console.log('Received task_id:', task_id);
        
        // 确保清除之前的轮询
        if (progressIntervalRef.current) {
          console.log('Clearing interval before setting new one...');
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        
        console.log('Setting up polling for task:', task_id);
        progressIntervalRef.current = setInterval(() => {
          pollAnalysisTaskStatus(task_id);
        }, 1000);
        
      } catch (error) {
        console.error('Error starting analysis:', error);
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        setIsProgressModalVisible(false);
        setIsAnalyzing(false);
        const errorMsg = error.response?.data?.error || error.message;
        message.error('启动分析失败: ' + errorMsg);
      }
    }
  };

  // 分镜生成
  const pollStoryboardTaskStatus = useCallback(async (taskId) => {
    try {
      console.log('Polling storyboard task status for:', taskId);
      const response = await api.get(`/generate-storyboard/status/${taskId}`);
      console.log('API response:', response.data);
      const { status, progress, result, error } = response.data;

      console.log('Task status:', status);
      console.log('Task progress:', progress);
      console.log('Task result:', result);
      console.log('Task error:', error);

      // 处理任务不存在的情况
      if (status === 'not_found') {
        console.log('Task not found, clearing interval...');
        if (storyboardIntervalRef.current) {
          clearInterval(storyboardIntervalRef.current);
          storyboardIntervalRef.current = null;
        }
        setIsStoryboardModalVisible(false);
        setIsGeneratingStoryboard(false);
        message.error('任务不存在或已过期，请重新生成分镜');
        return;
      }

      if (progress) {
        console.log('Updating storyboard progress:', progress);
        setStoryboardProgress(progress);
      }

      if (status === 'completed') {
        console.log('Task completed, clearing interval...');
        if (storyboardIntervalRef.current) {
          clearInterval(storyboardIntervalRef.current);
          storyboardIntervalRef.current = null;
        }
        console.log('Setting modal visibility...');
        setIsStoryboardModalVisible(false);
        setIsGeneratingStoryboard(false);

        console.log('Checking result:', result);
        if (result && result.shots) {
          console.log('Found shots:', result.shots.length, 'shots');
          try {
            console.log('Saving shots to database...');
            await shotApi.save(id, result.shots);
            console.log('Shots saved successfully');
            message.success(`分镜生成完成！共生成 ${result.shots.length} 个镜头`);
          } catch (saveError) {
            console.error('Error saving shots:', saveError);
            message.error('保存分镜失败: ' + (saveError.response?.data?.error || saveError.message));
          }
        } else {
          console.log('No shots found in result:', result);
          message.warning('分镜生成完成，但未生成任何镜头');
        }
      } else if (status === 'failed') {
        console.log('Task failed, clearing interval...');
        if (storyboardIntervalRef.current) {
          clearInterval(storyboardIntervalRef.current);
          storyboardIntervalRef.current = null;
        }
        setIsStoryboardModalVisible(false);
        setIsGeneratingStoryboard(false);

        // 检查是否是AI未配置错误或额度用完错误
        if (error && (error.includes('AI未配置') || error.includes('API密钥') || error.includes('FreeTierOnly') || error.includes('AllocationQuota') || error.includes('分析结果为空'))) {
          Modal.error({
            title: 'AI配置或额度问题',
            content: (
              <div>
                <p>{error}</p>
                <p>请前往「项目设置」页面配置大模型API密钥或检查额度后再试。</p>
              </div>
            ),
            okText: '去配置',
            onOk: () => {
              // 跳转到项目设置页面
              navigate(`/project/${id}/settings`);
            }
          });
        } else {
          message.error('分镜生成失败: ' + (error || '未知错误'));
        }
      }
    } catch (error) {
      console.error('获取分镜任务状态失败:', error);
      // 处理API调用失败的情况
      if (storyboardIntervalRef.current) {
        clearInterval(storyboardIntervalRef.current);
        storyboardIntervalRef.current = null;
      }
      setIsStoryboardModalVisible(false);
      setIsGeneratingStoryboard(false);
      message.error('获取分镜任务状态失败，请稍后重试');
    }
  }, [id]);

  // 直接生成分镜，不弹窗确认
  const handleGenerateStoryboard = async () => {
    if (!script.trim()) {
      message.warning('请先输入或上传剧本内容');
      return;
    }
    
    // 检查是否已存在分镜
    const hasShots = await checkExistingShots();
    
    if (hasShots) {
      Modal.confirm({
        title: '确认操作',
        content: '数据库里已经存在分镜，是否重新执行生成分镜命令？您的这个命令将会覆盖之前的数据。',
        okText: '确认执行',
        cancelText: '取消',
        onOk: async () => {
          setIsGeneratingStoryboard(true);
          setStoryboardProgress({ current: 0, total: 0, episode_title: '准备中...' });
          setIsStoryboardModalVisible(true);
          
          try {
            const startResponse = await api.post('/generate-storyboard/start', { 
              script,
              project_id: id
            });
            
            const { task_id, total_episodes } = startResponse.data;
            setStoryboardProgress({ current: 0, total: total_episodes, episode_title: '准备中...' });
            
            if (storyboardIntervalRef.current) {
              clearInterval(storyboardIntervalRef.current);
            }
            
            storyboardIntervalRef.current = setInterval(() => {
              pollStoryboardTaskStatus(task_id);
            }, 1000);
            
          } catch (error) {
            if (storyboardIntervalRef.current) {
              clearInterval(storyboardIntervalRef.current);
              storyboardIntervalRef.current = null;
            }
            setIsStoryboardModalVisible(false);
            setIsGeneratingStoryboard(false);
            const errorMsg = error.response?.data?.detail || error.message;
            message.error('启动分镜生成失败: ' + errorMsg);
          }
        }
      });
    } else {
      setIsGeneratingStoryboard(true);
      setStoryboardProgress({ current: 0, total: 0, episode_title: '准备中...' });
      setIsStoryboardModalVisible(true);
      
      try {
        const startResponse = await api.post('/generate-storyboard/start', { 
          script,
          project_id: id
        });
        
        const { task_id, total_episodes } = startResponse.data;
        setStoryboardProgress({ current: 0, total: total_episodes, episode_title: '准备中...' });
        
        if (storyboardIntervalRef.current) {
          clearInterval(storyboardIntervalRef.current);
        }
        
        storyboardIntervalRef.current = setInterval(() => {
          pollStoryboardTaskStatus(task_id);
        }, 1000);
        
      } catch (error) {
        if (storyboardIntervalRef.current) {
          clearInterval(storyboardIntervalRef.current);
          storyboardIntervalRef.current = null;
        }
        setIsStoryboardModalVisible(false);
        setIsGeneratingStoryboard(false);
        const errorMsg = error.response?.data?.detail || error.message;
        message.error('启动分镜生成失败: ' + errorMsg);
      }
    }
  };

  // 分镜组规划任务状态轮询
  const pollShotGroupsTaskStatus = useCallback(async (taskId) => {
    try {
      const response = await api.get(`/generate-shot-groups/status/${taskId}`);
      const { status, progress, result, error } = response.data;
      
      setShotGroupsProgress(progress);
      
      if (status === 'completed') {
        if (shotGroupsIntervalRef.current) {
          clearInterval(shotGroupsIntervalRef.current);
          shotGroupsIntervalRef.current = null;
        }
        setIsShotGroupsModalVisible(false);
        setIsGeneratingShotGroups(false);
        
        if (result && result.shot_groups) {
          await shotApi.save(id, result.shot_groups);
          message.success(`分镜组规划完成！共生成 ${result.shot_groups.length} 个分镜组`);
        }
      } else if (status === 'failed') {
        if (shotGroupsIntervalRef.current) {
          clearInterval(shotGroupsIntervalRef.current);
          shotGroupsIntervalRef.current = null;
        }
        setIsShotGroupsModalVisible(false);
        setIsGeneratingShotGroups(false);
        
        // 检查是否是AI未配置错误或额度用完错误
        if (error && (error.includes('AI未配置') || error.includes('API密钥') || error.includes('FreeTierOnly') || error.includes('AllocationQuota') || error.includes('分析结果为空'))) {
          Modal.error({
            title: 'AI配置或额度问题',
            content: (
              <div>
                <p>{error}</p>
                <p>请前往「项目设置」页面配置大模型API密钥或检查额度后再试。</p>
              </div>
            ),
            okText: '去配置',
            onOk: () => {
              navigate(`/project/${id}/settings`);
            }
          });
        } else {
          message.error('分镜组规划失败: ' + (error || '未知错误'));
        }
      }
    } catch (error) {
      console.error('获取分镜组任务状态失败:', error);
    }
  }, [id]);

  // 检查是否存在资产
  const checkExistingAssets = async () => {
    try {
      const response = await api.get(`/projects/${id}/assets`);
      return response.data && response.data.length > 0;
    } catch (error) {
      console.error('检查资产失败:', error);
      return false;
    }
  };

  // 检查是否存在分镜
  const checkExistingShots = async () => {
    try {
      const response = await shotApi.getByProjectId(id);
      return response && response.length > 0;
    } catch (error) {
      console.error('检查分镜失败:', error);
      return false;
    }
  };

  // 检查是否存在分镜组
  const checkExistingShotGroups = async () => {
    try {
      const response = await api.get(`/shots/project/${id}/groups`);
      return response.data && response.data.length > 0;
    } catch (error) {
      console.error('检查分镜组失败:', error);
      return false;
    }
  };

  // 分镜组规划
  const handleGenerateShotGroups = async () => {
    if (!script.trim()) {
      message.warning('请先输入或上传剧本内容');
      return;
    }
    
    // 检查是否已存在分镜组
    const hasShotGroups = await checkExistingShotGroups();
    
    if (hasShotGroups) {
      Modal.confirm({
        title: '确认操作',
        content: '数据库里已经存在分镜组，是否重新执行分镜组规划命令？您的这个命令将会覆盖之前的数据。',
        okText: '确认执行',
        cancelText: '取消',
        onOk: async () => {
          setIsGeneratingShotGroups(true);
          setShotGroupsProgress({ current: 0, total: 0, episode_title: '准备中...' });
          setIsShotGroupsModalVisible(true);
          
          try {
            const startResponse = await api.post('/generate-shot-groups/start', { 
              script,
              project_id: id
            });
            
            const { task_id, total_episodes } = startResponse.data;
            setShotGroupsProgress({ current: 0, total: total_episodes, episode_title: '准备中...' });
            
            if (shotGroupsIntervalRef.current) {
              clearInterval(shotGroupsIntervalRef.current);
            }
            
            shotGroupsIntervalRef.current = setInterval(() => {
              pollShotGroupsTaskStatus(task_id);
            }, 1000);
            
          } catch (error) {
            if (shotGroupsIntervalRef.current) {
              clearInterval(shotGroupsIntervalRef.current);
              shotGroupsIntervalRef.current = null;
            }
            setIsShotGroupsModalVisible(false);
            setIsGeneratingShotGroups(false);
            const errorMsg = error.response?.data?.detail || error.message;
            message.error('启动分镜组规划失败: ' + errorMsg);
          }
        }
      });
    } else {
      setIsGeneratingShotGroups(true);
      setShotGroupsProgress({ current: 0, total: 0, episode_title: '准备中...' });
      setIsShotGroupsModalVisible(true);
      
      try {
        const startResponse = await api.post('/generate-shot-groups/start', { 
          script,
          project_id: id
        });
        
        const { task_id, total_episodes } = startResponse.data;
        setShotGroupsProgress({ current: 0, total: total_episodes, episode_title: '准备中...' });
        
        if (shotGroupsIntervalRef.current) {
          clearInterval(shotGroupsIntervalRef.current);
        }
        
        shotGroupsIntervalRef.current = setInterval(() => {
          pollShotGroupsTaskStatus(task_id);
        }, 1000);
        
      } catch (error) {
        if (shotGroupsIntervalRef.current) {
          clearInterval(shotGroupsIntervalRef.current);
          shotGroupsIntervalRef.current = null;
        }
        setIsShotGroupsModalVisible(false);
        setIsGeneratingShotGroups(false);
        const errorMsg = error.response?.data?.detail || error.message;
        message.error('启动分镜组规划失败: ' + errorMsg);
      }
    }
  };

  // 辅助函数
  const getCharactersList = () => {
    if (!analysisResult?.characters) return [];
    return analysisResult.characters;
  };

  const getPropsList = () => {
    if (!analysisResult?.props) return [];
    return analysisResult.props;
  };

  const getScenesList = () => {
    if (!analysisResult?.scenes) return [];
    return analysisResult.scenes;
  };



  return (
    <div>
      {/* 返回按钮 */}
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(`/project/${id}`)}
        style={{ marginBottom: 16 }}
      >
        返回项目管理
      </Button>

      <Card
        title={
          <span>
            <span style={{ marginRight: 8 }}>剧本创作</span>
          </span>
        }
        extra={
          <div style={{ color: '#6c6c7d', fontSize: 12 }}>
            剧本由客户提供，不可修改
          </div>
        }
      >
        <div style={{ display: 'flex', gap: 16, height: '600px' }}>
          {/* 左侧：剧本编辑区域 */}
          <div style={{ flex: 3, display: 'flex', flexDirection: 'column' }}>
            <Tabs 
              activeKey={activeEpisode} 
              onChange={setActiveEpisode}
              type="card"
              style={{ marginBottom: 16, flex: 1 }}
            >
              <TabPane tab="全部" key="all">
                <TextArea
                  value={script}
                  readOnly
                  rows={25}
                  placeholder="在此输入或粘贴剧本内容，使用'第1集'、'第2集'等标识分集"
                  style={{
                    backgroundColor: '#1a1a24',
                    borderColor: '#2a2a3a',
                    color: '#e8e8ec',
                    height: '100%',
                    fontFamily: "'Microsoft YaHei', 'PingFang SC', monospace",
                    fontSize: 14,
                    lineHeight: 1.8
                  }}
                />
              </TabPane>
              {episodeList.map(episode => (
                <TabPane tab={episode} key={episode}>
                  <TextArea
                    value={episodes[episode] || ''}
                    rows={25}
                    readOnly
                    placeholder={`${episode}内容`}
                    style={{ 
                      backgroundColor: '#1a1a24', 
                      borderColor: '#2a2a3a', 
                      color: '#e8e8ec', 
                      height: '100%',
                      fontFamily: "'Microsoft YaHei', 'PingFang SC', monospace",
                      fontSize: 14,
                      lineHeight: 1.8
                    }}
                  />
                </TabPane>
              ))}
            </Tabs>
            
            {episodeList.length > 0 && (
              <div style={{ marginTop: 16, padding: 12, background: '#252532', borderRadius: 4, border: '1px solid #2a2a3a' }}>
                <strong style={{ color: '#e8e8ec' }}>分集统计：</strong>
                <span style={{ color: '#a0a0b0' }}>共 {episodeList.length} 集</span>
                {episodeList.map((ep, idx) => (
                  <span key={ep} style={{ marginLeft: 8, color: '#a0a0b0' }}>
                    {ep}({episodes[ep]?.length || 0}字符)
                    {idx < episodeList.length - 1 ? '、' : ''}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* 分析结果弹窗 */}
      <Modal
        title="剧本分析完成"
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setIsModalVisible(false)}>
            确定
          </Button>
        ]}
        width={500}
      >
        {analysisResult && (
          <div style={{ padding: '20px 0', color: '#e8e8ec' }}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
              <div style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 8, color: '#e8e8ec' }}>
                剧本分析完成！
              </div>
              <div style={{ color: '#a0a0b0' }}>
                已自动识别并添加以下资产到资产管理系统
              </div>
            </div>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-around', 
              padding: '20px', 
              background: '#252532', 
              borderRadius: 8,
              marginBottom: 20
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 32, fontWeight: 'bold', color: '#1890ff' }}>
                  {getCharactersList().length}
                </div>
                <div style={{ color: '#a0a0b0', marginTop: 4 }}>
                  <Tag color="blue">角色</Tag>
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 32, fontWeight: 'bold', color: '#52c41a' }}>
                  {getPropsList().length}
                </div>
                <div style={{ color: '#a0a0b0', marginTop: 4 }}>
                  <Tag color="green">道具</Tag>
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 32, fontWeight: 'bold', color: '#fa8c16' }}>
                  {getScenesList().length}
                </div>
                <div style={{ color: '#a0a0b0', marginTop: 4 }}>
                  <Tag color="orange">场景</Tag>
                </div>
              </div>
            </div>
            
            <div style={{ fontSize: 12, color: '#6c6c7d', textAlign: 'center' }}>
              <div>已自动合并重复项，并记录出现集数</div>
              <div style={{ marginTop: 4 }}>您可以在「资产管理」页面查看和管理这些资产</div>
            </div>
          </div>
        )}
      </Modal>

      {/* 分析进度条弹窗 */}
      <Modal
        title="正在分析剧本..."
        visible={isProgressModalVisible}
        closable={false}
        maskClosable={false}
        footer={null}
        width={500}
      >
        <div style={{ padding: '20px 0', color: '#e8e8ec' }}>
          <Progress
            percent={analysisProgress.total > 0 ? Math.round((analysisProgress.current / analysisProgress.total) * 100) : 0}
            status="active"
            strokeColor={{ from: '#108ee9', to: '#87d068' }}
          />
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 'bold', color: '#1890ff' }}>
              {analysisProgress.episode_title || '准备中...'}
            </div>
            <div style={{ marginTop: 8, color: '#a0a0b0' }}>
              进度: {analysisProgress.current} / {analysisProgress.total} 集
            </div>
          </div>
          <div style={{ marginTop: 16, padding: 12, background: '#252532', borderRadius: 4, fontSize: 12, color: '#a0a0b0' }}>
            <div>正在使用AI大模型分析剧本内容，识别角色、道具和场景...</div>
            <div style={{ marginTop: 4 }}>每集分析大约需要5-15秒，请耐心等待</div>
          </div>
        </div>
      </Modal>

      {/* 分镜生成进度弹窗 */}
      <Modal
        title="正在生成分镜..."
        visible={isStoryboardModalVisible}
        closable={false}
        maskClosable={false}
        footer={null}
        width={500}
      >
        <div style={{ padding: '20px 0', color: '#e8e8ec' }}>
          <Progress
            percent={storyboardProgress.total > 0 ? Math.round((storyboardProgress.current / storyboardProgress.total) * 100) : 0}
            status="active"
            strokeColor={{ from: '#722ed1', to: '#eb2f96' }}
          />
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 'bold', color: '#722ed1' }}>
              {storyboardProgress.episode_title || '准备中...'}
            </div>
            <div style={{ marginTop: 8, color: '#a0a0b0' }}>
              进度: {storyboardProgress.current} / {storyboardProgress.total} 集
            </div>
          </div>
          <div style={{ marginTop: 16, padding: 12, background: '#252532', borderRadius: 4, fontSize: 12, color: '#a0a0b0' }}>
            <div>正在使用AI大模型分析剧本场景，生成分镜规划...</div>
            <div style={{ marginTop: 4 }}>每集分析大约需要10-30秒，请耐心等待</div>
          </div>
        </div>
      </Modal>

      {/* 分镜组规划进度弹窗 */}
      <Modal
        title="正在生成分镜组规划..."
        visible={isShotGroupsModalVisible}
        closable={false}
        maskClosable={false}
        footer={null}
        width={500}
      >
        <div style={{ padding: '20px 0', color: '#e8e8ec' }}>
          <Progress
            percent={shotGroupsProgress.total > 0 ? Math.round((shotGroupsProgress.current / shotGroupsProgress.total) * 100) : 0}
            status="active"
            strokeColor={{ from: '#1890ff', to: '#52c41a' }}
          />
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 'bold', color: '#1890ff' }}>
              {shotGroupsProgress.episode_title || '准备中...'}
            </div>
            <div style={{ marginTop: 8, color: '#a0a0b0' }}>
              进度: {shotGroupsProgress.current} / {shotGroupsProgress.total} 集
            </div>
          </div>
          <div style={{ marginTop: 16, padding: 12, background: '#252532', borderRadius: 4, fontSize: 12, color: '#a0a0b0' }}>
            <div>正在使用AI大模型分析剧本，生成分镜组规划...</div>
            <div style={{ marginTop: 4 }}>每个分镜组约10-15秒，每集分析大约需要10-30秒</div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ScriptPage;
